/**
 * Where a visitor came from on this visit: campaign parameters (`utm_*`), a referral code
 * (`ref`), the landing page and the referring site. Kept in memory only (nothing on the device,
 * so no consent needed) and sent once with sign-up, which stores it on the account
 * (users.attribution). The website's links into the app carry its own `utm_*`/`ref`.
 */
export type Attribution = Partial<Record<(typeof PARAMS)[number], string>> & {
  landing: string;
  referrer: string;
  firstSeen: string;
};

/** Older versions kept it in localStorage. */
const LEGACY_KEY = "attribution";
const PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ref",
] as const;

let current: Attribution | null = null;

/** Remembers how this visit started (call once at start, before the router changes the URL). */
export function captureAttribution(
  location: Pick<Location, "search" | "pathname" | "hostname"> = window.location,
  referrer = document.referrer,
) {
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // storage unavailable: nothing to clean up
  }
  const params = new URLSearchParams(location.search);
  const campaign = Object.fromEntries(
    PARAMS.flatMap((name) => {
      const value = params.get(name)?.trim().slice(0, 100);
      return value ? [[name, value]] : [];
    }),
  );
  let from = "";
  try {
    from = referrer ? new URL(referrer).hostname : "";
  } catch {
    // not a URL
  }
  current = {
    ...campaign,
    landing: location.pathname.slice(0, 200),
    // Our own pages are not a source.
    referrer: from === location.hostname ? "" : from,
    firstSeen: new Date().toISOString(),
  };
}

export function getAttribution(): Attribution | null {
  return current;
}
